"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import ThemeToggle from "./ThemeToggle";
import {
    Menu,
    Home,
    TabletSmartphone,
    Sparkles,
    Brush,
    AtSign,
    FileText,
} from "lucide-react";

/* Navigation Items with Icons */
const navItems = [
    { name: "Home", href: "#hero", icon: Home },
    { name: "Projects", href: "#projects", icon: TabletSmartphone },
    { name: "Skills", href: "#skills", icon: Sparkles },
    { name: "Designs", href: "#case-studies", icon: Brush },
    { name: "Contact", href: "#contact", icon: AtSign },
];

interface MobileNavbarProps {
    onOpenChange?: (isOpen: boolean) => void;
}

export default function MobileNavbar({ onOpenChange }: MobileNavbarProps) {
    const [isOpen, setIsOpen] = useState(false);
    const navRef = useRef<HTMLDivElement>(null);

    // Notify parent when open state changes
    useEffect(() => {
        onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                navRef.current &&
                !navRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Close navbar on scroll
    useEffect(() => {
        const handleScroll = () => {
            if (isOpen) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            window.addEventListener("scroll", handleScroll, { passive: true });
        }

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [isOpen]);

    const scrollToSection = (
        e: React.MouseEvent<HTMLAnchorElement>,
        href: string
    ) => {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
        setIsOpen(false);
    };

    return (
        <div className="md:hidden">
            {/* Floating Burger Button */}
            <AnimatePresence>
                {!isOpen && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                        onClick={() => setIsOpen(true)}
                        className="fixed top-6 right-6 z-50 w-12 h-12 rounded-full bg-surface border border-border shadow-md flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow"
                        aria-label="Open navigation menu"
                    >
                        <Menu className="w-5 h-5 text-foreground" strokeWidth={2.5} />
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Backdrop */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
                        aria-hidden="true"
                    />
                )}
            </AnimatePresence>

            {/* Slide-in Navbar - Floating Pill Design */}
            <AnimatePresence>
                {isOpen && (
                    <motion.nav
                        ref={navRef}
                        initial={{ x: "100%", y: "-50%" }}
                        animate={{ x: 0, y: "-50%" }}
                        exit={{ x: "100%", y: "-50%" }}
                        transition={{
                            type: "spring",
                            damping: 25,
                            stiffness: 300,
                        }}
                        className="fixed top-1/2 right-4 w-[140px] max-h-[85vh] overflow-y-auto bg-background/95 backdrop-blur-md border border-border rounded-4xl shadow-xl z-50 flex flex-col py-6 px-4 no-scrollbar"
                        aria-label="Mobile navigation"
                    >
                        {/* Avatar Section */}
                        <Link
                            href="#hero"
                            onClick={(e) => scrollToSection(e, "#hero")}
                            className="flex flex-col items-center gap-2 group"
                        >
                            <div className="relative w-14 h-14 rounded-full overflow-hidden border border-border">
                                <Image
                                    src="/avatar.png"
                                    alt="Hasan avatar"
                                    fill
                                    className="object-cover"
                                    sizes="56px"
                                />
                            </div>
                            <span className="text-xs text-text-secondary text-center leading-tight group-hover:text-foreground transition-colors">
                                Glad you&apos;re
                                <br />
                                here
                            </span>
                        </Link>

                        {/* Spacer after avatar */}
                        <div className="h-8 shrink-0" />

                        {/* Navigation Links */}
                        <ul className="flex flex-col items-center gap-6">
                            {navItems.map((link) => {
                                const IconComponent = link.icon;
                                return (
                                    <li key={link.name}>
                                        <Link
                                            href={link.href}
                                            onClick={(e) =>
                                                scrollToSection(e, link.href)
                                            }
                                            className="flex flex-col items-center gap-1 text-text-secondary hover:text-accent transition-colors duration-200 group"
                                        >
                                            <IconComponent
                                                className="w-6 h-6"
                                                strokeWidth={1.5}
                                            />
                                            <span className="text-xs font-medium">
                                                {link.name}
                                            </span>
                                        </Link>
                                    </li>
                                );
                            })}
                            {/* Resume Link */}
                            <li>
                                <a
                                    href="/api/resume/download?type=view"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-col items-center gap-1 text-text-secondary hover:text-accent transition-colors duration-200 group"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <FileText className="w-6 h-6" strokeWidth={1.5} />
                                    <span className="text-xs font-medium">Resume</span>
                                </a>
                            </li>
                        </ul>

                        {/* Theme Toggle - Inside the pill */}
                        <div className="flex justify-center mt-8">
                            <ThemeToggle />
                        </div>

                        {/* Spacer before logo */}
                        {/* <div className="h-2 shrink-0" /> */}

                        {/* HSN. Logo */}
                        {/* <div className="text-center">
                            <span className="text-sm font-serif text-text-secondary/40 tracking-wider">
                                HSN.
                            </span>
                        </div> */}
                    </motion.nav>
                )}
            </AnimatePresence>
        </div>
    );
}
